import React, { useState, useRef } from 'react';
import CubeRenderer from './CubeRenderer.tsx';
import './CubeRenderer.css';

const CubeGenerator: React.FC = () => {
  const [gridSize, setGridSize] = useState({ width: 5, height: 5 });
  const [grid, setGrid] = useState<number[][]>([
    [0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0]
  ]);
  const [currentHeight, setCurrentHeight] = useState(1);
  const [isErasing, setIsErasing] = useState(false);

  // Initialize grid when size changes
  const initializeGrid = (width: number, height: number) => {
    const newGrid = Array(height).fill(null).map(() => Array(width).fill(0));
    setGrid(newGrid);
  };

  // Handle grid size changes
  const handleGridSizeChange = (dimension: 'width' | 'height', value: number) => {
    const newSize = { ...gridSize, [dimension]: value };
    setGridSize(newSize);
    initializeGrid(newSize.width, newSize.height);
  };

  // Handle grid cell click
  const handleCellClick = (row: number, col: number) => {
    const newGrid = [...grid];
    
    if (isErasing) {
      // Erasing mode - reduce height by 1 or set to 0
      newGrid[row][col] = Math.max(0, newGrid[row][col] - 1);
    } else {
      // Building mode - set to current height or add 1
      newGrid[row][col] = currentHeight;
    }
    
    setGrid(newGrid);
  };

  // Handle grid cell right-click for adding height
  const handleCellRightClick = (row: number, col: number, e: React.MouseEvent) => {
    e.preventDefault();
    const newGrid = [...grid];
    newGrid[row][col] = Math.min(10, newGrid[row][col] + 1); // Max height of 10
    setGrid(newGrid);
  };

  // Clear all cubes
  const clearGrid = () => {
    const newGrid = Array(gridSize.height).fill(null).map(() => Array(gridSize.width).fill(0));
    setGrid(newGrid);
  };

  // Get total cube count
  const getTotalCubes = () => {
    return grid.flat().reduce((sum, height) => sum + height, 0);
  };

  // Render control grid
  const renderControlGrid = () => {
    return (
      <div className="control-grid" style={{ 
        display: 'grid', 
        gridTemplateColumns: `repeat(${gridSize.width}, 40px)`,
        gap: '2px',
        margin: '20px 0',
        justifyContent: 'center'
      }}>
        {grid.map((row, rowIndex) =>
          row.map((height, colIndex) => (
            <div
              key={`${rowIndex}-${colIndex}`}
              className="grid-cell"
              style={{
                width: '40px',
                height: '40px',
                border: '1px solid #ccc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                backgroundColor: height > 0 ? `hsl(200, 50%, ${100 - height * 8}%)` : '#f9f9f9',
                color: height > 0 ? '#333' : '#999',
                fontWeight: 'bold',
                fontSize: '12px',
                transition: 'all 0.2s'
              }}
              onClick={() => handleCellClick(rowIndex, colIndex)}
              onContextMenu={(e) => handleCellRightClick(rowIndex, colIndex, e)}
              title={`Row ${rowIndex + 1}, Col ${colIndex + 1} - Height: ${height} (Left click: set height, Right click: add +1)`}
            >
              {height || ''}
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <div className="cube-generator" style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h2>3D Cube Generator</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '15px' }}>
          <label>
            Grid Width: 
            <select 
              value={gridSize.width} 
              onChange={(e) => handleGridSizeChange('width', parseInt(e.target.value))}
              style={{ marginLeft: '5px' }}
            >
              {[2,3,4,5,6,7,8].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
          
          <label>
            Grid Height: 
            <select 
              value={gridSize.height} 
              onChange={(e) => handleGridSizeChange('height', parseInt(e.target.value))}
              style={{ marginLeft: '5px' }}
            >
              {[2,3,4,5,6,7,8].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>

        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '15px' }}>
          <label>
            Cube Height: 
            <select 
              value={currentHeight} 
              onChange={(e) => setCurrentHeight(parseInt(e.target.value))}
              style={{ marginLeft: '5px' }}
            >
              {[1,2,3,4,5,6,7,8,9,10].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
          
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input 
              type="checkbox" 
              checked={isErasing} 
              onChange={(e) => setIsErasing(e.target.checked)}
            />
            Erasing Mode
          </label>
          
          <button 
            onClick={clearGrid}
            style={{
              padding: '8px 16px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Clear All
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <p><strong>Instructions:</strong></p>
        <ul style={{ fontSize: '14px', lineHeight: '1.4' }}>
          <li><strong>Left click</strong> a grid cell to set it to the selected height</li>
          <li><strong>Right click</strong> a grid cell to add +1 to its height</li>
          <li>Use <strong>Erasing Mode</strong> to reduce heights by clicking</li>
          <li>Numbers in cells show the height (number of stacked cubes)</li>
          <li>Total cubes: <strong>{getTotalCubes()}</strong></li>
        </ul>
      </div>

      {/* Control Grid */}
      <div>
        <h3>Grid Control (Click to build)</h3>
        {renderControlGrid()}
      </div>

      {/* 3D Visualization */}
      <div>
        <h3>3D Visualization</h3>
        <CubeRenderer grid={grid} size={500} />
      </div>
    </div>
  );
};

export default CubeGenerator;